/**
 * TimeGridView — the hour grid, for one day or seven.
 *
 * PLANNER-1b. The chrome is Rennan's Planner artboard: a 56px gutter,
 * 48px hours drawn as one repeating gradient rather than 24 elements,
 * and events as filled blocks in their own colour with white type. The
 * behaviour underneath is CAL-PRO's, and the two meet because the
 * artboard is a drawing and the engine is the part a drawing cannot
 * do:
 *
 *   · OVERLAPS. The artboard puts every event at `left: 3px; right:
 *     3px`, so two shoots at eleven draw on top of each other.
 *     `layoutDay` splits the column — clustered transitively, columns
 *     reused the moment they free — and the 3px inset survives as the
 *     seam between neighbours.
 *   · DRAG. Move, stretch either edge, or draw a new block on empty
 *     space (`useGridDrag`).
 *   · BOOKABLE HOURS, shaded behind everything from
 *     `availability_rules`.
 *   · MIDNIGHT. An event that runs past it appears on both days,
 *     clipped, with the corner removed where it carries on.
 *
 * THE WINDOW
 *
 * The artboard shows 6am to 10pm and no scrollbar, which is the right
 * default — a day you can see all of beats a day you scroll. Taken
 * literally it would also HIDE a 5am call, so the window opens as far
 * as it has to: the artboard's hours, widened to cover anything
 * outside them. A normal week looks exactly as drawn; a week with a
 * dawn flight shows the dawn.
 *
 * `days` is the whole difference between week and day: seven dates or
 * one. Two components that both draw a day drift within a month.
 *
 * This renders in the planner's tokens (`planner.css`) and expects to
 * be inside `.plan-app`.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { format, isToday } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  gapsAround,
  layoutDay,
  type GridInput,
  type MinuteBand,
  type Span,
} from './timeGrid';
import { useGridDrag, type DragPreview, type GridPoint } from './useGridDrag';
import type { DetailedCalendarEvent } from './EventDetailsModal';

type GridEvent = DetailedCalendarEvent & GridInput;

/** The artboard's hour. */
const HOUR_PX = 48;
/** The artboard's gutter. */
const GUTTER_PX = 56;
const MINUTES = 24 * 60;
/** The artboard's window, before the day's own events widen it. */
const WINDOW_FROM = 6;
const WINDOW_TO = 22;
/** Below this a block cannot hold its own title. */
const MIN_BLOCK_PX = 22;
/** The least of the drag ghost that stays on screen. */
const GHOST_MIN_PX = 14;
/** How close to the edge a drag scrolls the page, and how fast. */
const EDGE_PX = 52;
const EDGE_MAX_PX = 16;
/** A block shorter than this has no room for a time under its title. */
const TIME_MIN_MINUTES = 40;
/** Seven columns need this much before they stop being columns. */
const WEEK_MIN_PX = 640;

const hourLabel = (hr: number) =>
  hr === 0 ? '12 AM' : hr < 12 ? `${hr} AM` : hr === 12 ? '12 PM' : `${hr - 12} PM`;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export interface TimeGridViewProps {
  /** One date for the day view, seven for the week. */
  days: Date[];
  events: DetailedCalendarEvent[];
  /** Per-type colours. The Calendars list owns these. */
  colorOf: (event: DetailedCalendarEvent) => string;
  onEventClick: (event: DetailedCalendarEvent) => void;
  /** CAL-KEY-1 — the chip the keyboard is pointed at. */
  selectedId?: string | null;
  /** A day's heading was clicked — the week view drills into it. */
  onPickDay?: (day: Date) => void;
  /** Tapping an empty hour. Omit to make the grid read-only. */
  onAddAtTime?: (at: Date) => void;
  /** An event was dragged or stretched. The grid reports; the page writes. */
  onEventMove?: (event: DetailedCalendarEvent, next: Span) => void;
  /** A span was drawn on empty space. */
  onCreateRange?: (next: Span) => void;
  /** Whether this event may be moved. Without it, nothing is draggable. */
  canDragEvent?: (event: DetailedCalendarEvent) => boolean;
  /** Hours a client can book, or null to shade nothing. Must be stable. */
  bookableHours?: (day: Date) => MinuteBand[] | null;
  /** The day view's heading bar, as the artboard draws it. */
  dayLabel?: string;
  daySummary?: string;
  className?: string;
}

export function TimeGridView({
  days,
  events,
  colorOf,
  onEventClick,
  selectedId,
  onPickDay,
  onAddAtTime,
  onEventMove,
  onCreateRange,
  canDragEvent,
  bookableHours,
  dayLabel,
  daySummary,
  className,
}: TimeGridViewProps) {
  const body = useRef<HTMLDivElement>(null);
  const gutter = useRef<HTMLDivElement>(null);
  const ghost = useRef<HTMLDivElement>(null);
  const ghostLabel = useRef<HTMLSpanElement>(null);
  const week = days.length > 1;

  // The engine wants `start`/`end`; the app has `startAt`/`endAt`.
  const gridEvents = useMemo<GridEvent[]>(
    () => events.map((e) => ({ ...e, start: e.startAt, end: e.endAt })),
    [events],
  );

  const columns = useMemo(
    () =>
      days.map((day) => {
        const open = bookableHours?.(day);
        return { day, ...layoutDay(gridEvents, day), closed: open ? gapsAround(open) : [] };
      }),
    [bookableHours, days, gridEvents],
  );

  /**
   * The hours on show: the artboard's, widened by anything outside
   * them, so the default looks exactly as drawn and nothing is ever
   * hidden. `Placed` carries percentages of the whole day, which is
   * what these divisions undo.
   */
  const { fromHour, toHour } = useMemo(() => {
    let a = WINDOW_FROM;
    let b = WINDOW_TO;
    for (const col of columns) {
      for (const p of col.timed) {
        a = Math.min(a, Math.floor(((p.top / 100) * MINUTES) / 60));
        b = Math.max(b, Math.ceil((((p.top + p.height) / 100) * MINUTES) / 60));
      }
    }
    return { fromHour: Math.max(0, a), toHour: Math.min(24, Math.max(b, a + 1)) };
  }, [columns]);

  const fromMin = fromHour * 60;
  const windowMin = (toHour - fromHour) * 60;
  const gridH = (toHour - fromHour) * HOUR_PX;
  /** Minutes past midnight → pixels down the column. */
  const toPx = useCallback((min: number) => ((min - fromMin) / 60) * HOUR_PX, [fromMin]);

  const hours = useMemo(
    () => Array.from({ length: toHour - fromHour }, (_, i) => fromHour + i),
    [fromHour, toHour],
  );

  /* ───────────────────────── geometry ───────────────────────── */

  /**
   * Measured fresh every frame of a drag. The window can widen while
   * one is in progress — drag something to 5am and the grid grows an
   * hour under the pointer — and a cached rect is a ghost that drifts.
   */
  const measure = useCallback(() => {
    const el = body.current;
    const g = gutter.current;
    if (!el || !g) return null;
    // The gutter is the grid's first cell: its box gives both the
    // y origin (it is exactly as tall as the columns beside it) and
    // the x where the columns start. The body's padding never enters
    // the arithmetic that way.
    const rect = g.getBoundingClientRect();
    const full = el.getBoundingClientRect();
    const colWidth = (full.right - rect.right) / days.length;
    if (colWidth <= 0 || rect.height <= 0) return null;
    return {
      top: rect.top,
      height: rect.height,
      left: rect.right,
      colWidth,
      gutterW: rect.width,
      // The body's own top padding. Every `toPx` is measured from the
      // GUTTER's top, and the ghost is positioned against the body's
      // padding box, so without this it sits a dozen pixels high.
      padTop: rect.top - full.top,
    };
  }, [days.length]);

  const resolve = useCallback(
    (clientX: number, clientY: number): GridPoint | null => {
      const m = measure();
      if (!m) return null;
      const down = fromMin + ((clientY - m.top) / m.height) * windowMin;
      return {
        dayIndex: Math.max(0, Math.min(days.length - 1, Math.floor((clientX - m.left) / m.colWidth))),
        // Clamped to the WINDOW, not to the day: above the grid is its
        // first hour, below it its last. Letting it run past either
        // would move an event to a time the grid is not showing.
        minutes: Math.max(fromMin, Math.min(fromMin + windowMin, down)),
      };
    },
    [days.length, fromMin, measure, windowMin],
  );

  /** Scroll the page when a drag reaches the top or bottom of the view. */
  const autoScroll = useCallback((clientY: number) => {
    const h = window.innerHeight;
    if (clientY < EDGE_PX) {
      window.scrollBy(0, -((EDGE_PX - clientY) / EDGE_PX) * EDGE_MAX_PX);
    } else if (clientY > h - EDGE_PX) {
      window.scrollBy(0, ((clientY - (h - EDGE_PX)) / EDGE_PX) * EDGE_MAX_PX);
    }
  }, []);

  const onPreview = useCallback(
    (p: DragPreview | null) => {
      const el = ghost.current;
      if (!el) return;
      const m = p ? measure() : null;
      if (!p || !m) {
        el.style.display = 'none';
        return;
      }
      const column = startOfDay(days[p.dayIndex]);
      const startMin = (p.start.getTime() - column.getTime()) / 60000;
      const spanMin = (p.end.getTime() - p.start.getTime()) / 60000;

      // Clipped against the window exactly as a real block is.
      let top = toPx(startMin);
      let height = (spanMin / 60) * HOUR_PX;
      if (top < 0) {
        height += top;
        top = 0;
      }
      top = Math.min(top, m.height - GHOST_MIN_PX);
      height = Math.min(Math.max(height, GHOST_MIN_PX), m.height - top);

      el.style.display = 'block';
      el.style.left = `${m.gutterW + p.dayIndex * m.colWidth + 3}px`;
      el.style.width = `${m.colWidth - 6}px`;
      el.style.top = `${top + m.padTop}px`;
      el.style.height = `${height}px`;
      if (ghostLabel.current) {
        ghostLabel.current.textContent = `${format(p.start, 'h:mm a')} – ${format(p.end, 'h:mm a')}`;
      }
    },
    [days, measure, toPx],
  );

  /* ─────────────────────────── drag ─────────────────────────── */

  const byId = useMemo(() => {
    const m = new Map<string, DetailedCalendarEvent>();
    events.forEach((e) => m.set(e.id, e));
    return m;
  }, [events]);

  const onCommit = useCallback(
    (c: { kind: string; id?: string; start: Date; end: Date }) => {
      if (c.kind === 'create') {
        onCreateRange?.({ start: c.start, end: c.end });
        return;
      }
      const event = c.id ? byId.get(c.id) : undefined;
      if (!event) return;
      // A drag that landed back where it started is not a write, a
      // toast and a refetch.
      if (event.startAt.getTime() === c.start.getTime() && event.endAt.getTime() === c.end.getTime()) {
        return;
      }
      onEventMove?.(event, { start: c.start, end: c.end });
    },
    [byId, onCreateRange, onEventMove],
  );

  const { begin, active, swallowClick } = useGridDrag({ resolve, onPreview, onCommit, autoScroll });

  const canMove = (e: DetailedCalendarEvent) =>
    !!onEventMove && !e.allDay && (canDragEvent?.(e) ?? false);
  const canCreate = !!onCreateRange;

  // The window can change under a live drag; the ghost is repainted
  // every frame anyway, but a resize between frames would leave it
  // stale, so hide it whenever the geometry moves out from under it.
  useEffect(() => {
    if (ghost.current) ghost.current.style.display = 'none';
  }, [fromHour, toHour, days.length]);

  const anyAllDay = columns.some((c) => c.allDay.length > 0);
  const tracks = `${GUTTER_PX}px repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <section className={cn('plan-panel plan-wrap', active && 'plan-dragging', className)}>
      <div style={{ minWidth: week ? WEEK_MIN_PX : undefined }}>
        {/* The day view's heading bar. */}
        {!week && (dayLabel || daySummary) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              padding: '14px 20px',
              borderBottom: '1px solid var(--line)',
            }}
          >
            {dayLabel && <div style={{ fontSize: 14, fontWeight: 600 }}>{dayLabel}</div>}
            {daySummary && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{daySummary}</div>}
          </div>
        )}

        {/* Day headings. The week view drills in from them. */}
        {week && (
          <div style={{ display: 'grid', gridTemplateColumns: tracks, borderBottom: '1px solid var(--line)' }}>
            <div />
            {columns.map(({ day }) => (
              <button
                key={day.toISOString()}
                type="button"
                className={cn('plan-cell', isToday(day) && 'today')}
                style={{ borderTop: 0, display: 'flex', alignItems: 'center', gap: 8, padding: 10 }}
                onClick={() => onPickDay?.(day)}
                aria-label={format(day, 'EEEE, MMMM d')}
              >
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{format(day, 'EEE')}</span>
                <span className="plan-num plan-tnum">{format(day, 'd')}</span>
              </button>
            ))}
          </div>
        )}

        {/* All-day banner. Takes no room when nothing is in it. */}
        {anyAllDay && (
          <div style={{ display: 'grid', gridTemplateColumns: tracks, borderBottom: '1px solid var(--line)' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: 8,
                fontSize: 11,
                color: 'var(--muted)',
              }}
            >
              All day
            </div>
            {columns.map(({ day, allDay }) => (
              <div key={day.toISOString()} style={{ minWidth: 0, padding: 6, display: 'grid', gap: 4 }}>
                {allDay.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onEventClick(e)}
                    className={cn('plan-allday', selectedId === e.id && 'sel')}
                    style={{ background: colorOf(e) }}
                  >
                    {e.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        <div
          ref={body}
          style={{
            display: 'grid',
            gridTemplateColumns: tracks,
            padding: '12px 0 16px',
            // The ghost is absolute against this.
            position: 'relative',
          }}
        >
          <div ref={gutter} style={{ position: 'relative', height: gridH }}>
            {hours.map((hr) => (
              <div
                key={hr}
                className="plan-tnum"
                style={{
                  position: 'absolute',
                  right: 8,
                  top: (hr - fromHour) * HOUR_PX,
                  fontSize: 11,
                  color: 'var(--muted)',
                  transform: 'translateY(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                {hourLabel(hr)}
              </div>
            ))}
          </div>

          {columns.map(({ day, timed, closed }) => {
            const now = isToday(day) ? new Date() : null;
            const nowMin = now ? now.getHours() * 60 + now.getMinutes() : null;
            const nowPx =
              nowMin !== null && nowMin >= fromMin && nowMin <= fromMin + windowMin ? toPx(nowMin) : null;
            return (
              <div key={day.toISOString()} className="plan-hours" style={{ position: 'relative', height: gridH }}>
                {/* Hours nobody can book. Drawn as the gaps, because a
                    working week is mostly bookable and the quieter mark
                    is the one worth making. */}
                {closed.map((band) => {
                  const top = Math.max(0, toPx(band.from));
                  const bottom = Math.min(gridH, toPx(band.to));
                  if (bottom <= top) return null;
                  return (
                    <div
                      key={`closed-${band.from}`}
                      aria-hidden
                      className="plan-closed"
                      style={{ top, height: bottom - top }}
                    />
                  );
                })}

                {/* The hours themselves, as buttons under everything so
                    a tap on a block never also opens "new event at 3".
                    Invisible: the column's gradient draws the lines. */}
                {hours.map((hr) => (
                  <button
                    key={hr}
                    type="button"
                    className="plan-slot"
                    disabled={!onAddAtTime && !canCreate}
                    draggable={false}
                    onPointerDown={canCreate ? (ev) => begin(ev, 'create', { day }) : undefined}
                    onClick={
                      onAddAtTime
                        ? () => {
                            if (swallowClick()) return;
                            const at = new Date(day);
                            at.setHours(hr, 0, 0, 0);
                            onAddAtTime(at);
                          }
                        : undefined
                    }
                    aria-label={
                      onAddAtTime ? `Add at ${hourLabel(hr)} on ${format(day, 'EEEE d MMMM')}` : undefined
                    }
                    style={{ top: (hr - fromHour) * HOUR_PX, height: HOUR_PX }}
                  />
                ))}

                {timed.map(({ event, top, height, left, width, clippedStart, clippedEnd }) => {
                  const startMin = (top / 100) * MINUTES;
                  const minutes = (height / 100) * MINUTES;
                  const movable = canMove(event);
                  const held = active?.kind === 'move' && active.id === event.id;
                  const origin: Span = { start: event.startAt, end: event.endAt };
                  const px = Math.max(MIN_BLOCK_PX, (minutes / 60) * HOUR_PX - 3);
                  return (
                    <button
                      key={event.id}
                      type="button"
                      draggable={false}
                      onPointerDown={movable ? (ev) => begin(ev, 'move', { id: event.id, origin }) : undefined}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (swallowClick()) return;
                        onEventClick(event);
                      }}
                      title={event.title}
                      className={cn(
                        'plan-ev',
                        movable && 'movable',
                        held && 'held',
                        selectedId === event.id && 'sel',
                      )}
                      style={{
                        top: toPx(startMin) + 1,
                        height: px,
                        // The artboard's 3px inset, kept as the seam
                        // between two neighbours the engine split.
                        left: `calc(${left}% + 3px)`,
                        width: `calc(${width}% - 6px)`,
                        background: colorOf(event),
                        borderTopLeftRadius: clippedStart ? 0 : undefined,
                        borderTopRightRadius: clippedStart ? 0 : undefined,
                        borderBottomLeftRadius: clippedEnd ? 0 : undefined,
                        borderBottomRightRadius: clippedEnd ? 0 : undefined,
                      }}
                    >
                      <span className="t">{event.title}</span>
                      {minutes >= TIME_MIN_MINUTES && (
                        <span style={{ fontSize: 11, display: 'block', opacity: 0.86 }}>
                          {format(event.startAt, 'h:mm a')} – {format(event.endAt, 'h:mm a')}
                        </span>
                      )}

                      {/* The edges, shown only under the pointer. */}
                      {movable && !clippedStart && px >= 44 && (
                        <span
                          role="presentation"
                          className="plan-grip top"
                          onPointerDown={(ev) => begin(ev, 'resize-start', { id: event.id, origin })}
                        />
                      )}
                      {movable && !clippedEnd && (
                        <span
                          role="presentation"
                          className="plan-grip bottom"
                          onPointerDown={(ev) => begin(ev, 'resize-end', { id: event.id, origin })}
                        />
                      )}
                    </button>
                  );
                })}

                {nowPx !== null && <div className="plan-now" style={{ top: nowPx }} />}
              </div>
            );
          })}

          {/* The drag ghost. Mounted always and hidden, so the first
              frame has something to write to; nothing in React moves
              it. Absolute against the grid above. */}
          <div ref={ghost} aria-hidden className="plan-ghost" style={{ display: 'none' }}>
            <span ref={ghostLabel} className="plan-tnum" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default TimeGridView;
